import { ApiError, NetworkError, ProblemDetailsSchema, setErrorRoute, TimeoutError, TransientAuthError } from '../error';
import type { ResolvedRequestOptions } from '../generated/client';
import { client } from '../generated/client.gen';
import type { RecaptchaHeaders } from '../generated/recaptcha.gen';

/**
 * Hey-API port of `src/client.ts` (`customFetch`). Each concern customFetch owned
 * maps to a client seam: timeout → `fetch` wrapper; recaptcha/step-up headers →
 * request interceptor; 401→refresh→retry→redirect → response interceptor;
 * ApiError taxonomy → error interceptor. See ADR-022 / HEY-API-MIGRATION.md.
 */

// customFetch took these as a second argument; Hey-API has no such slot, so they
// travel through the `meta` bag: `listOrders({ meta: { stepUp: true } })`.
//
// RecaptchaHeaders is generated (see libs/api/plugins/recaptcha.ts): its `action`
// is the union of actions the BFF actually declares, so a mistyped action fails
// here rather than as a 403 action_mismatch. Kept optional on RequestMeta because
// most operations are ungated — the generated `*WithRecaptcha` wrappers are what
// make it mandatory for the ones that aren't.
export interface RequestMeta {
  recaptcha?: RecaptchaHeaders;
  stepUp?: boolean;
  // Suppress the 401 *redirect* (not the refresh) for this one request. Per-request, so a concurrent
  // request that 401s is unaffected — unlike `withSkipUnauthorized`, whose module-global counter spans
  // the whole wrapped promise. Preferred for probes that treat 401 as a domain signal (anonymous/absent).
  skipUnauthorized?: boolean;
}

// Teach the generated SDK about the meta we thread through. hey-api types the SDK `meta` param as
// `keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta`, so augmenting the (empty,
// augmentable) generated `ClientMeta` interface makes `meta` strongly typed at every call site
// instead of silently degrading to `Record<string, unknown>`. The empty-bodied `extends` interface
// relies on `noEmptyInterface` being off for this lib (see libs/api/biome.json).
declare module '../generated/core/types.gen' {
  interface ClientMeta extends RequestMeta {}
}

const RECAPTCHA_TOKEN_HEADER = 'X-Recaptcha-Token';
const RECAPTCHA_ACTION_HEADER = 'X-Recaptcha-Action';
const STEP_UP_HEADER = 'X-Step-Up-Token';
const PAGE_HEADER = 'X-Hub-Page';
const REFRESH_ENDPOINT = '/api/auth/refresh-session';
const REFRESH_TIMEOUT_MS = 10_000;
const REFRESH_LOCK = 'session-refresh';
const SESSION_RETRY_BACKOFF_MS = [300, 600, 1800];

/** Equal jitter — floor of base/2, ceiling up to base — so a coalesced refresh's
 *  released requests don't retry in lockstep. */
const jitter = (base: number) => base / 2 + Math.random() * (base / 2);

let stepUpToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
let pageProvider: (() => string | null) | null = null;
let redirecting = false;
let skipDepth = 0;

export function setStepUpToken(token: string | null) {
  stepUpToken = token;
}

/**
 * Registers the source of the `X-Waipu-Page` header, which tells the BFF which page an
 * API call came from so its Sentry events can say so. A provider rather than a value,
 * because the answer changes on every navigation; and injected rather than read here,
 * because the router belongs to the app and this lib must not depend on it.
 *
 * Return the matched *route pattern*, not the resolved URL: the BFF puts it in an
 * indexed tag, so `/konto/rechnungen/$invoiceId` keeps that bounded where a real path
 * would not — and a pattern cannot leak an id or a token.
 */
export function registerPageProvider(provider: (() => string | null) | null) {
  pageProvider = provider;
}

export function registerUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
  redirecting = false;
}

/**
 * Run `fn` with the 401 redirect suppressed — the refresh still runs, but a terminal 401 surfaces as an
 * `ApiError` instead of navigating. For speculative work: `definePageRoute` wraps hover-preload
 * queryFns, and the session probe treats 401 as "anonymous".
 *
 * The scope spans until a returned promise settles, because the redirect decision is taken in an async
 * response interceptor — a synchronous-only scope would already be gone by then. Consequence: a
 * *concurrent* request that 401s inside the window is suppressed too, so keep wrapped work short.
 */
export function withSkipUnauthorized<T>(fn: () => T): T {
  skipDepth++;
  let result: T;
  try {
    result = fn();
  } catch (error) {
    skipDepth--;
    throw error;
  }
  if (result instanceof Promise) {
    // Cast: `T` is the promise type itself, which `.finally` preserves.
    return result.finally(() => {
      skipDepth--;
    }) as T;
  }
  skipDepth--;
  return result;
}

type RefreshResult = 'success' | 'terminal' | 'transient';
let refreshPromise: Promise<RefreshResult> | null = null;

// Raw `fetch`, not the client, so a 401 here can't re-enter the refresh loop.
async function doRefresh(): Promise<RefreshResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const response = await fetch(REFRESH_ENDPOINT, { method: 'POST', credentials: 'include', signal: controller.signal });
    if (response.ok) {
      return 'success';
    }
    return response.status === 401 ? 'terminal' : 'transient';
  } catch {
    // Network failure or our timeout: transient IdP blip, never a forced logout.
    return 'transient';
  } finally {
    clearTimeout(timeoutId);
  }
}

// Two coalescing layers: refreshPromise collapses concurrent 401s within the tab,
// the Web Lock serializes refreshes across tabs.
function refreshSession(): Promise<RefreshResult> {
  refreshPromise ??= runExclusive(doRefresh).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function runExclusive(fn: () => Promise<RefreshResult>): Promise<RefreshResult> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) {
    return fn();
  }
  try {
    return await locks.request(REFRESH_LOCK, fn);
  } catch {
    // locks.request can reject (storage-partitioned / non-fully-active context);
    // degrade to the per-tab promise rather than propagating a raw lock error.
    return fn();
  }
}

const delay = (ms: number, signal?: AbortSignal | null) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(signal.reason);
      },
      { once: true },
    );
  });

// The original request's body stream is consumed once fetched; serializedBody is
// captured pre-flight, so mutations retry correctly too.
function cloneForRetry(request: Request, opts: ResolvedRequestOptions): Request {
  return new Request(request.url, {
    method: request.method,
    headers: request.headers,
    body: opts.serializedBody ?? undefined,
    credentials: 'include',
    signal: request.signal,
  });
}

// `meta` is threaded through at runtime but typed only at the SDK layer, not on
// the base ResolvedRequestOptions — hence the read-side cast.
const readMeta = (opts: ResolvedRequestOptions): RequestMeta | undefined => (opts as { meta?: RequestMeta }).meta;

const injectRequestHeaders = (request: Request, opts: ResolvedRequestOptions): Request => {
  const meta = readMeta(opts);
  const page = pageProvider?.() ?? null;
  // Cloning a Request is not free, so only pay for it when there is something to add.
  if (!meta?.recaptcha && !meta?.stepUp && !page) {
    return request;
  }
  const next = new Request(request);
  if (meta?.recaptcha) {
    next.headers.set(RECAPTCHA_TOKEN_HEADER, meta.recaptcha.token);
    next.headers.set(RECAPTCHA_ACTION_HEADER, meta.recaptcha.action);
  }
  if (meta?.stepUp && stepUpToken) {
    next.headers.set(STEP_UP_HEADER, stepUpToken);
  }
  if (page) {
    next.headers.set(PAGE_HEADER, page);
  }
  return next;
};

const handleUnauthorized = async (response: Response, request: Request, opts: ResolvedRequestOptions): Promise<Response> => {
  if (response.status !== 401) {
    return response;
  }
  // Captured before the first await: the scope may close while the refresh is in flight. The global
  // `skipDepth` (withSkipUnauthorized) covers speculative preloads; per-request `meta.skipUnauthorized`
  // covers probes that own their SDK call and read 401 as a domain signal, without the concurrency bleed.
  const skipRedirect = skipDepth > 0 || readMeta(opts)?.skipUnauthorized === true;
  const callerSignal = request.signal;
  const doFetch = opts.fetch ?? globalThis.fetch;

  let current = response;
  let refreshed = false;
  let backoffAttempt = 0;

  while (current.status === 401) {
    if (!refreshed) {
      refreshed = true;
      const result = await refreshSession();
      if (callerSignal?.aborted) {
        throw callerSignal.reason;
      }
      if (result === 'success') {
        current = await doFetch(cloneForRetry(request, opts));
        continue;
      }
      if (result === 'transient') {
        throw new TransientAuthError();
      }
      // 'terminal' → redirect / ApiError below.
    } else if (backoffAttempt < SESSION_RETRY_BACKOFF_MS.length) {
      // Refresh succeeded but the rotated session may still be propagating.
      await delay(jitter(SESSION_RETRY_BACKOFF_MS[backoffAttempt++] ?? 0), callerSignal);
      current = await doFetch(cloneForRetry(request, opts));
      continue;
    }

    if (onUnauthorized && !skipRedirect) {
      if (!redirecting) {
        redirecting = true;
        try {
          onUnauthorized();
        } catch (handlerError) {
          redirecting = false;
          throw handlerError;
        }
      }
      // Never settle: navigation tears down the tree; resolving would surface the
      // transition as data or an error to the consumer.
      return new Promise<Response>(() => {
        /* intentionally never settled */
      });
    }
    // No handler (or skipped): let the client's non-ok path throw → ApiError(401).
    return current;
  }

  return current;
};

const toApiError = (
  error: unknown,
  response: Response | undefined,
  request: Request | undefined,
  options: ResolvedRequestOptions,
): unknown => {
  // A bare TypeError *after* the response arrived means the body stream died mid-read (the headers
  // already arrived, so timeoutFetch — which wraps only fetch() — saw no error): a genuine transport
  // failure. Guarding on `response` matters: a TypeError with no response comes from our own request
  // pipeline (a header-injection or URL-building bug), not the network — mislabelling it NetworkError
  // would retry it and hide it from Sentry, so let it fall through to the generic Error path below.
  if (error instanceof TypeError && response) {
    return new NetworkError(error, request?.url ?? response.url, request?.method ?? 'GET');
  }
  // The client throws an HTTP failure as its *parsed body* — a plain object or string, never an Error.
  // So anything that is already an Error came from us (ApiError/Timeout/TransientAuth/Network), from a
  // caller abort, or from a callback we invoked (a throwing unauthorized handler). Relabelling those as
  // an HTTP error would bury the real failure behind the status of the response that triggered it.
  // timeoutFetch doesn't know the route template; add it for the Sentry tag.
  if (error instanceof TimeoutError) {
    return setErrorRoute(error, options.url);
  }
  if (error instanceof Error) {
    return error;
  }
  if (!response) {
    return error;
  }
  const parsed = ProblemDetailsSchema.safeParse(error);
  // `options.url` is the un-interpolated path template (pinned by the transport spec).
  return new ApiError(
    response.status,
    parsed.success ? parsed.data : {},
    response.headers,
    request?.url ?? response.url,
    request?.method ?? 'GET',
    options.url,
  );
};

let installed = false;

/**
 * Call once at app bootstrap. Only interceptors: `baseUrl`/`credentials`/`fetch` are applied when the
 * client is constructed, via `runtime.ts` (see the ordering note there).
 *
 * Forgetting this call leaves requests working but unguarded — no refresh, no redirect, no ApiError
 * mapping — so `apps/hub/src/main.tsx` is the single expected caller.
 */
export function installInterceptors() {
  if (installed) {
    return;
  }
  installed = true;

  client.interceptors.request.use(injectRequestHeaders);
  client.interceptors.response.use(handleUnauthorized);
  client.interceptors.error.use(toApiError);
}
